import { Fragment, useEffect, useRef, useState, type ReactNode } from "react";
import { formatInstitutionSummary, toInstitutionValue } from "@shared/institution";
import { formatAddressSummary } from "@shared/address";
import type { Ch2Cell, Ch2Rows } from "@shared/interview/board-ch2";
import { gridTracks } from "@shared/interview/board";
import { FIELD_KEYS } from "@shared/constants/fields";
import { InstitutionInput } from "@/components/InstitutionInput";
import { AddressInput } from "@/components/AddressInput";
import { CELL_BOX, DateCell, IconTextCell, OffCell, PickerCell, SegmentCell } from "../InterviewCells";
import { ContactCard, LinkedPersonCell, slotName, type CardSlot } from "../PersonPicker";
import { LocationBox, LocationBoxPanel } from "../LocationBox";
import { InterviewRowTable, type RowTableRow } from "../InterviewRowTable";
import { saveInBackground } from "../saveFailure";
import { usePanels } from "../useOnePanelOpen";
import { OtherBox, withTypedOther } from "../OtherBox";
import { isMoneyCell } from "@shared/interview/board-map";
import { branchWordOf, fieldOf, fieldsOf, isDrawn, isOff, optionsOf, OTHER_WORD, storedOf, wordOf } from "./ch2Bindings";

/**
 * s232 (chapter two, PR B) — THE CHAPTER-TWO ENGINE: a tile's stacked `lines` and its `rows` table, drawn from the
 * board's declarations (`Chapter2.dc.html`, read at run time through ./board-ch2 — rule 137) and written through the
 * map (./ch2Bindings → board-map.ts).
 *
 * 🔴 It draws from the interview's ONE control set (rule 47): the chapter-one cells, the contact card, the location
 * box, the institution type-ahead and the six-field address. Nothing here is a second control.
 *
 * What the board declares and this draws: a line's label, hint, width and placeholder; a yes/no or picker whose
 * answer opens its `then` lines under it; a person line whose follow-ups show once someone is named; "Left to
 * someone?" (rule 55) with its card; `offWhen`/`unless` switching a line off ("not asked"); the answer's note.
 * A cell drawn but not built (board-map.ts DRAWN_CELLS — the insurer lines, ruling 5) is left out with its branch.
 */

/** What one row gives the engine: its values and the screen's write paths (all through the section's one row surface). */
export type Ch2RowIO = {
  rowId: string;
  values: Readonly<Record<string, string>>;
  specify?: Readonly<Record<string, string>>;
  save: (changes: Record<string, string>) => Promise<unknown>;
  saveSpecify: (key: string, text: string) => Promise<unknown>;
  fieldIdByKey: ReadonlyMap<string, string>;
  clientId: string;
  /** The s225 build requirement: skip "Was it meant as a gift?" when the co-owner is the planner's married spouse. */
  skipGift?: (values: Readonly<Record<string, string>>) => boolean;
  /**
   * s233 (D-233.81, board v440 `pinTo`): the planner's own home, pinned as row 1 of its table — no ×; an owned home's
   * "What is it?" offers Primary residence (stored as the plan's "Primary Residence"); a rented home asks its table's
   * `pinLines` in place of the table's second line.
   */
  pinned?: boolean;
  /** s233 (rule 146): this row's own mark, when it is not its table's (the pinned home row takes its tile's). */
  mark?: { mark: string; color: string };
  /**
   * s235 PR B (D-235.65): the planner's OLD home after "Where you live" switched — "Do you still own <address>?" (or
   * rent) under the row, Keep · Remove. Keep makes it an ordinary place; Remove is the row's own delete.
   */
  stillHere?: { question: string; onKeep: () => void; onRemove: () => void };
  /**
   * s237 PR 5 (D-237.52): the home row's one line under it — "Your home — owned / rented, from Personal details." and the
   * link that opens Personal details (Change it there).
   */
  homeLine?: { text: string; change: string; onChange: () => void };
};

/** s233 (D-233.81): the pinned owned home's kind — the board's word for the stored "Primary Residence". */
export const PRIMARY_RESIDENCE = { value: "Primary Residence", label: "Primary residence" } as const;

/** s235 PR B (D-235.65): "Do you still own / rent <address>? Keep · Remove" — the old home's one line. s237 (rule 159): ONE
 *  piece, drawn under a chapter-two table row and under chapter one's "Is this home…". */
export function StillHereLine({ question, onKeep, onRemove, testId }: { question: string; onKeep: () => void; onRemove: () => void; testId: string }) {
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid={testId}>
      <span className="text-[14px] text-warm-charcoal">{question}</span>
      <button type="button" onClick={onKeep} className="h-11 rounded-[8px] border border-warm-stone bg-warm-white px-[18px] text-[13px] text-warm-charcoal hover:bg-warm-stone-light"
        data-testid={`${testId}-keep`}>Keep</button>
      <button type="button" onClick={onRemove} className="h-11 rounded-[8px] border border-warm-stone bg-warm-white px-[18px] text-[13px] text-warm-charcoal hover:bg-warm-stone-light"
        data-testid={`${testId}-remove`}>Remove</button>
    </div>
  );
}

type Where = { screen: string; tile: string; testId: string };

const lineKey = (w: Where, io: Ch2RowIO, l: Ch2Cell) => `${io.rowId}:${w.tile}.${l.k ?? l.kind}`;
/**
 * s233 (D-233.81): a money box is known by the board's KEY, not its words — v440 renamed "Roughly worth" to "Estimated
 * value" and "Roughly a month" to "Monthly rent" (rule 148), and a label test would have lost the dollars.
 */
// s235 (D-235.18(2), D-235.19): a rent deposit's amount ("How much" since D-236.74) is money too.
// s237: the money keys are ONE declaration (board-map.ts MONEY_CELL_KEYS), shared with the tile-to-row engine.
const isMoney = (c: Pick<Ch2Cell, "k">) => isMoneyCell(c.k);
/** s233: a paired line is half the row, less half the 14px gap (`gap-x-3.5`) between the two. */
export const PAIR_WIDTH = "calc(50% - 7px)";

/**
 * The options a picker shows: the board's, plus — when the plan holds an answer the board does not offer (a classic
 * page's "Investment Property") — that answer as itself, so an existing answer is shown, never blanked (G8).
 */
function withHeld(options: { value: string; label: string }[], held: string): { value: string; label: string }[] {
  return held && !options.some((o) => o.value === held) ? [...options, { value: held, label: held }] : options;
}

/** The card for a person line or cell: the name, then its phone, email and address (rule 63 — s235 PR A binds all three
 *  on every chapter-two card, each the linked person's own column). */
export function slotsFor(fields: readonly string[], org?: boolean): CardSlot[] {
  const [name, phone, email, address, askName, askPhone, askEmail, askAddress] = fields;
  // s235 PR B (D-235.18(3)): the lender's name is TEXT that names a person (D-233.32(2)(3)) — the doctor's shape, so an
  // existing lender's words are shown, never blanked (G8); every other name is a contact.
  const first: CardSlot = { label: org ? "The company" : "Person 1", name: TEXT_NAMED.has(name) ? { kind: "text", key: name } : { kind: "contact", key: name }, phone, email, address, org };
  // s235 PR B (rule 150, findings item 63): a company card's "+ Add a person to ask for" — the school card's shape
  // (DayToDayScreen.tsx), the person linked to that company by reference.
  if (!askName) return [first];
  return [first, { label: "A person to ask for", name: { kind: "contact", key: askName }, phone: askPhone, email: askEmail, address: askAddress }];
}

/** s235 PR B: the names that are text naming a person (the lender), not a contact value. */
const TEXT_NAMED: ReadonlySet<string> = new Set([FIELD_KEYS.MORTGAGE_LENDER, FIELD_KEYS.LOAN_LENDER]);
/** s235 PR B (rule 150): the company card's second person, as the school card words it. */
export const ASK_FOR_LABEL = "+ Add a person to ask for";

// s237: the "Other — type it" box and its words are the shared piece (../OtherBox, rule 159).

/** The institution type-ahead over a TEXT field: the words are stored, the suggestion is only help (s201). */
function InstCell({ value, placeholder, onCommit, testId, fieldId }: { value: string; placeholder?: string; onCommit: (v: string) => void; testId: string; fieldId: string }) {
  const [draft, setDraft] = useState(toInstitutionValue(value));
  useEffect(() => setDraft(toInstitutionValue(value)), [value]);
  const latest = useRef(draft);
  latest.current = draft;
  return (
    <div data-testid={testId}>
      <InstitutionInput interviewLook value={draft} fieldId={fieldId} placeholder={placeholder}
        onChange={(v) => { latest.current = v; setDraft(v); }}
        onBlur={() => { const words = formatInstitutionSummary(latest.current).trim(); if (words !== value) onCommit(words); }} />
    </div>
  );
}

/** A money box — the interview's ONE (s233, D-233.34): dollars shown, digits stored, words never guessed. */
function MoneyCell({ value, placeholder, onCommit, testId, ariaLabel }: { value: string; placeholder?: string; onCommit: (v: string) => void; testId: string; ariaLabel?: string }) {
  return <IconTextCell money value={value} placeholder={placeholder} ariaLabel={ariaLabel} testId={testId} onCommit={onCommit} />;
}

/** The six-field address, opened under its cell with Done (rules 28 and 50). */
function AddressPanel({ value, fieldId, onCommit, onClose, testId }: { value: string; fieldId: string; onCommit: (v: string) => Promise<unknown>; onClose: () => void; testId: string }) {
  const [draft, setDraft] = useState(value);
  const latest = useRef(value);
  return (
    <div className="mb-3 flex max-w-[760px] flex-col gap-3 rounded-[8px] border border-warm-stone bg-warm-white p-4" data-testid={`${testId}-panel`}>
      {/* s235 PR B (D-235.34): the board's open address under a table cell is the card's look (Chapter2.dc.html:304-307 —
          street 2fr with the pin + "Unit or suite" 1fr, then four across, cream boxes). */}
      <AddressInput interviewLook="card" value={draft} fieldId={fieldId} onChange={(v) => { latest.current = v; setDraft(v); }}
        onBlur={() => { if (latest.current !== value) saveInBackground(onCommit(latest.current)); }} />
      <div>
        <button type="button" onClick={() => { if (latest.current !== value) saveInBackground(onCommit(latest.current)); onClose(); }}
          className="h-11 rounded-[8px] bg-sage-600 px-5 text-[14px] text-warm-white hover:bg-sage-800" data-testid={`${testId}-done`}>Done</button>
      </div>
    </div>
  );
}

/** s234 (D-233.70(5), rule 149): "Person named in will" — Yes names someone (rule 55); No and Not sure name no one. */
export const WILL_NAMES_WORD = "Yes";

/** "Person named in will" — Yes · No · Not sure. Only Yes opens the name card; its name lands in the cell (rule 55). */
function willControl(w: Where, io: Ch2RowIO, cell: Ch2Cell, panels: ReturnType<typeof usePanels>) {
  const [leftKey, personKey, ...details] = fieldsOf(w.screen, w.tile, cell);
  const word = wordOf(w.screen, w.tile, cell, io.values[leftKey] ?? "");
  const named = word === WILL_NAMES_WORD;
  const cardKey = `${lineKey(w, io, cell)}:card`;
  // s235 PR A (rule 63): the will person's card carries Mobile · Email · an address too.
  const slots: CardSlot[] = slotsFor([personKey, ...details]);
  const name = slotName(slots[0], io.values);
  const pick = (stored: string) => {
    const names = wordOf(w.screen, w.tile, cell, stored) === WILL_NAMES_WORD;
    saveInBackground(io.save({ [leftKey]: stored }));
    if (names) { if (!panels.isOpen(cardKey)) panels.toggle(cardKey); } else if (panels.isOpen(cardKey)) panels.close(cardKey);
  };
  // s235 PR C (D-235.92, rule 149): the name lands in the cell — the NAME ONLY (the heading already says "Person named in
  // will"), drawn as the contact name that wraps to three lines and then cuts (D-234.91); it opens the person's card. Until
  // someone is named, the cell is the Yes / No / Not sure picker, and Yes asks who on the line under the row. Clearing the
  // name in the card brings the picker back.
  const who = (
    <LinkedPersonCell value={io.values[personKey] ?? ""} word={cell.word ?? ""} open={panels.isOpen(cardKey)} onOpen={() => panels.toggle(cardKey)}
      testId={`${w.testId}-will-who`} />
  );
  const picker = named && name ? who : (
    <PickerCell value={io.values[leftKey] ?? ""} options={optionsOf(w.screen, w.tile, cell)} placeholder={cell.ph ?? "Choose…"} ariaLabel={cell.label ?? "Person named in will"}
      onCommit={pick} testId={`${w.testId}-will`} />
  );
  const trigger = named && !name ? who : null;
  const card = named && panels.isOpen(cardKey) ? (
    <ContactCard interviewLook key={cardKey} title={(cell.cardTitles ?? [""])[0] ?? ""} slots={slots} values={io.values} fieldIdByKey={io.fieldIdByKey}
      clientId={io.clientId} onSave={io.save} onClose={() => panels.close(cardKey)} testId={`${w.testId}-will-card`} />
  ) : null;
  return { picker, trigger, card };
}

/**
 * A tile's stacked lines (the home's owned-place block, a rented home, a table row's follow-ups). Each line keeps
 * the board's width; a revealed branch, a card and a writing space each take their own full line under the question
 * (s210: "a follow-up sits under its own question").
 */
export function Ch2Lines({ where, lines, io, noteFor, onNote, pairs = false, oneLine = false, packed = false }: {
  where: Where;
  lines: readonly Ch2Cell[];
  io: Ch2RowIO;
  noteFor?: Record<string, string | Record<string, string>>;
  /** The note an answer shows (the board's `noteFor`), reported to the block that draws it. */
  onNote?: (note: string) => void;
  /**
   * s233 (D-233.16 point 1, D-233.35(7); the board's `pairs`): short answers sit two to a line, in order; a long answer
   * (a location box) keeps its own line; each pair's follow-ups sit directly under the pair, paired among themselves.
   */
  pairs?: boolean;
  /**
   * s233 (D-233.81, board v440 `oneLine`; D-233.54(1)): the block's questions — and the follow-ups an answer opens — stay on
   * ONE line; a card or panel any of them opens goes under the whole line, never between its questions.
   */
  oneLine?: boolean;
  /**
   * s237 PR 5 (D-237.53, board v465 `:1297`): a table row's second line — every question first, then ONE break, then all
   * their follow-ups together on one line (End date beside How much); the line wraps only when they do not fit.
   */
  packed?: boolean;
}) {
  const panels = usePanels();
  const notes: string[] = [];
  const out: ReactNode[] = [];
  const deferred: ReactNode[] = [];
  const full = (key: string, node: ReactNode) => (oneLine ? deferred : out).push(<div key={key} className="basis-full">{node}</div>);

  // s233 (D-233.54(1)): in pairs mode a follow-up branch is not walked inline — it is collected here and laid out after
  // its pair (its own lines paired), so a card a follow-up opens sits under the whole pair, never between its two halves.
  let capture: (readonly Ch2Cell[])[] | null = null;
  const walk = (ls: readonly Ch2Cell[], depth: number) => {
    for (const l of ls) {
      if (l.kind === "break") { out.push(<div key={`br-${out.length}`} className="basis-full" />); continue; }
      if (isDrawn(where.screen, where.tile, l)) continue;
      if (l.k === "gift" && io.skipGift?.(io.values)) continue;
      const key = lineKey(where, io, l);
      const f = fieldOf(where.screen, where.tile, l);
      const v = io.values[f] ?? "";
      const tid = `${where.testId}-${l.k ?? l.kind}`;
      const wrap = (control: ReactNode, extra?: ReactNode) => (
        // A yes/no the board gives no width fits its answers (its three can be long: "Jointly — it goes to them").
        <div key={key} className="flex min-w-0 flex-col gap-1.5" data-testid={`${tid}-line`}
          // s237 PR 5 (D-237.49, board v465 `:1351`): a choice takes only the room its buttons need — no stretched buttons.
          style={pairs && !(l.kind === "text" && l.big) ? { width: PAIR_WIDTH, maxWidth: "100%" } : l.kind === "seg" && !l.w ? { maxWidth: "100%" } : { width: l.w ?? "200px", maxWidth: "100%" }}>
          {l.label && <span className="text-[14px] text-warm-charcoal">{l.label}</span>}
          {control}
          {l.hint && <span className="text-[13px] text-warm-muted">{l.hint}</span>}
          {extra}
        </div>
      );
      if (isOff(where.screen, where.tile, l, lines, io.values)) { out.push(wrap(<OffCell testId={tid} />)); continue; }

      if (l.kind === "text" && l.big) {
        out.push(<div key={`${key}-brk`} className="basis-full" />);
        out.push(wrap(<LocationBox value={v} placeholder={l.text} open={panels.isOpen(`${key}:big`)} onOpen={() => panels.toggle(`${key}:big`)}
          onCommit={(t) => io.save({ [f]: t })} testId={tid} />));
        if (panels.isOpen(`${key}:big`)) full(`${key}-panel`, <LocationBoxPanel title={l.label ?? ""} hint={l.hint} placeholder={l.text} value={v}
          onCommit={(t) => io.save({ [f]: t })} onClose={() => panels.close(`${key}:big`)} testId={tid} />);
        continue;
      }
      if (l.kind === "text") {
        out.push(wrap(isMoney(l)
          ? <MoneyCell value={v} placeholder={l.text} ariaLabel={l.label} onCommit={(t) => saveInBackground(io.save({ [f]: t }))} testId={tid} />
          : <IconTextCell value={v} placeholder={l.text} ariaLabel={l.label} onCommit={(t) => saveInBackground(io.save({ [f]: t }))} testId={tid} />));
        continue;
      }
      if (l.kind === "inst") {
        out.push(wrap(<InstCell value={v} placeholder={l.text} fieldId={io.fieldIdByKey.get(f) ?? f} onCommit={(t) => saveInBackground(io.save({ [f]: t }))} testId={tid} />));
        continue;
      }
      if (l.kind === "date") {
        out.push(wrap(<DateCell value={v} ariaLabel={l.label} onCommit={(d) => saveInBackground(io.save({ [f]: d }))} testId={tid} />));
        continue;
      }
      if (l.kind === "person") {
        const fields = fieldsOf(where.screen, where.tile, l);
        const slots = slotsFor(fields, l.org);
        out.push(wrap(<LinkedPersonCell value={io.values[fields[0]] ?? ""} word={l.word ?? ""} org={l.org} open={panels.isOpen(`${key}:card`)}
          onOpen={() => panels.toggle(`${key}:card`)} testId={tid} />));
        if (panels.isOpen(`${key}:card`)) full(`${key}-card`, <ContactCard interviewLook title={l.title ?? l.label ?? ""} slots={slots} values={io.values}
          moreLabel={slots.length > 1 ? ASK_FOR_LABEL : undefined}
          fieldIdByKey={io.fieldIdByKey} clientId={io.clientId} onSave={io.save} onClose={() => panels.close(`${key}:card`)} testId={`${tid}-card`} />);
        // s225: a person line's follow-ups show once someone is being named (the board: card open, or named).
        if (Array.isArray(l.then) && (slotName(slots[0], io.values) || panels.isOpen(`${key}:card`)) && capture) { capture.push(l.then); continue; }
        if (Array.isArray(l.then) && (slotName(slots[0], io.values) || panels.isOpen(`${key}:card`))) {
          if (!oneLine) out.push(<div key={`${key}-then-a`} className="basis-full" />);
          walk(l.then, depth + 1);
          if (!oneLine) out.push(<div key={`${key}-then-b`} className="basis-full" />);
        }
        continue;
      }
      if (l.kind === "will") {
        const will = willControl(where, io, l, panels);
        out.push(wrap(will.picker, will.trigger));
        if (will.card) full(`${key}-card`, will.card);
        continue;
      }
      if (l.kind === "seg" || l.kind === "picker") {
        const word = wordOf(where.screen, where.tile, l, v);
        out.push(wrap(l.kind === "seg"
          ? <SegmentCell fit={!pairs && !l.w} value={v} options={optionsOf(where.screen, where.tile, l)} ariaLabel={l.label} onCommit={(s) => saveInBackground(io.save({ [f]: s }))} testId={tid} />
          : <PickerCell value={v} options={withHeld(optionsOf(where.screen, where.tile, l), v)} placeholder={l.ph} ariaLabel={l.label} onCommit={(s) => saveInBackground(io.save({ [f]: s }))} testId={tid} />));
        const nf = noteFor?.[l.k ?? ""];
        if (word && nf) {
          if (typeof nf === "object") { if (nf[word]) notes.push(nf[word]); }
          else if (word !== "No" && word !== "Not sure") notes.push(nf);
        }
        // s234 walk (item B): the open branch may come from another answer (an Investment Property opens Rented out?'s Yes).
        const open = branchWordOf(where.screen, where.tile, l, io.values);
        const branch = open && l.then && !Array.isArray(l.then) ? l.then[open] : undefined;
        if (branch?.length && capture) { capture.push(branch); continue; }
        if (branch?.length) {
          if (!oneLine) out.push(<div key={`${key}-then-a`} className="basis-full" />);
          walk(branch, depth + 1);
          if (!oneLine) out.push(<div key={`${key}-then-b`} className="basis-full" />);
        }
        continue;
      }
    }
  };
  if (pairs) {
    // Each line is walked on its own: its first node is the question; its card or panel goes under the pair it sits in;
    // its follow-up questions come after that, paired among themselves (s233, D-233.35(7), D-233.54(1)).
    const brk = () => <div className="basis-full" />;
    const pairBlock = (ls: readonly Ch2Cell[]) => {
      let heads: ReactNode[] = [];
      let under: ReactNode[] = [];
      let branches: (readonly Ch2Cell[])[] = [];
      const flush = () => {
        if (!heads.length && !under.length && !branches.length) return;
        if (heads.length) out.push(...heads, brk());
        if (under.length) out.push(...under, brk());
        const next = branches;
        heads = []; under = []; branches = [];
        for (const b of next) pairBlock(b);
      };
      for (const l of ls) {
        if (l.kind === "break") { flush(); continue; }
        const start = out.length;
        const caught: (readonly Ch2Cell[])[] = [];
        capture = caught;
        walk([l], 0);
        capture = null;
        const seg = out.splice(start);
        if (!seg.length) continue;
        if (l.kind === "text" && l.big) { flush(); out.push(...seg, brk()); continue; }
        heads.push(seg[0]);
        under.push(...seg.slice(1));
        branches.push(...caught);
        if (heads.length === 2) flush();
      }
      flush();
    };
    pairBlock(lines);
  } else if (packed) {
    const caught: (readonly Ch2Cell[])[] = [];
    for (const l of lines) {
      capture = caught;
      walk([l], 0);
      capture = null;
    }
    if (caught.length) {
      out.push(<div key="packed-break" className="basis-full" />);
      for (const b of caught) walk(b, 1);
    }
  } else walk(lines, 0);
  if (deferred.length) out.push(<div key="one-line-end" className="basis-full" />, ...deferred);

  const note = notes[notes.length - 1] ?? "";
  useEffect(() => { onNote?.(note); }, [note, onNote]);
  return <div className="flex flex-wrap items-start gap-x-3.5 gap-y-4" data-testid={where.testId}>{out.map((n, i) => <Fragment key={i}>{n}</Fragment>)}</div>;
}


/** A table row's picked answer whose `then` opens lines under the row (a loan → its lender). */
function thenOf(w: Where, cell: Ch2Cell, values: Readonly<Record<string, string>>): readonly Ch2Cell[] {
  const word = branchWordOf(w.screen, w.tile, cell, values);
  return word && cell.then && !Array.isArray(cell.then) ? cell.then[word] ?? [] : [];
}

/**
 * A tile's table (`rows`): one board row per record. The first cell sits in the lead column; a `lines` cell is the
 * row's own follow-up block under it (the owned-place questions); a picked answer's `then` follows; a drawn column
 * is left out with its grid track.
 */
export function Ch2Table({ screen, tile, rows, data, lead, mark, onAdd, onRemove, testId, noteFor, onNote }: {
  screen: string;
  tile: string;
  /** s233 (rule 146): the tile's mark and colour — every row starts with it. */
  mark?: { mark: string; color: string };
  rows: Ch2Rows;
  data: readonly Ch2RowIO[];
  lead?: string;
  onAdd: () => void;
  onRemove: (rowId: string) => void;
  testId: string;
  noteFor?: Record<string, string | Record<string, string>>;
  onNote?: (note: string) => void;
}) {
  const panels = usePanels();
  const tracks = gridTracks(rows.cols);
  const columnCells = rows.cells.filter((c) => c.kind !== "lines");
  const keep = columnCells.map((c) => !isDrawn(screen, tile, c));
  const gridTemplate = [...tracks.slice(0, columnCells.length).filter((_, i) => keep[i]), tracks[tracks.length - 1]].join(" ");
  const headers = rows.headers.filter((_, i) => keep[i]);
  const where = (io: Ch2RowIO): Where => ({ screen, tile, testId: `${testId}-${io.rowId}` });

  const cellFor = (c: Ch2Cell, io: Ch2RowIO): { node: ReactNode; under: ReactNode[] } => {
    const w = where(io);
    const f = fieldOf(screen, tile, c);
    const v = io.values[f] ?? "";
    const key = `${io.rowId}:${tile}.${c.k}`;
    const tid = `${w.testId}-${c.k}`;
    const label = rows.headers[columnCells.indexOf(c)]?.label ?? "";
    const under: ReactNode[] = [];
    // s234 (CH2-STORAGE-PR3): a column the board switches off (`offWhen` — the will when the place is held jointly) reads
    // "not asked", as a line does; its trigger may sit in the row's own lines (the tenure), so those are searched too.
    if (c.offWhen && isOff(screen, tile, c, [...rows.cells, ...lineCellsOf(rows, io).flatMap((x) => x.lines ?? [])], io.values)) {
      return { node: <OffCell testId={tid} />, under };
    }
    if (c.kind === "picker" && c.k === "will") {
      const will = willControl(w, io, c, panels);
      if (will.trigger) under.push(<div key={`${key}-who`} className="flex max-w-[420px] flex-col gap-1.5"><span className="text-[14px] text-warm-charcoal">{(c.cardTitles ?? [""])[0]}</span>{will.trigger}</div>);
      if (will.card) under.push(<div key={`${key}-card`}>{will.card}</div>);
      return { node: will.picker, under };
    }
    if (c.kind === "picker") {
      // s234 walk (item C): ONE list for every owned row — Primary residence is offered on each, not only the pinned home.
      const listed = c.k === "kind" && f === FIELD_KEYS.PROP_TYPE ? [PRIMARY_RESIDENCE, ...optionsOf(screen, tile, c)] : optionsOf(screen, tile, c);
      // s235 PR C (D-235.87): once words are typed in the Type it box, the cell reads them in place of "Other — type it";
      // the list still offers every choice (the typed words stand in for Other's own line). One rule, every picker here.
      const typed = wordOf(screen, tile, c, v) === OTHER_WORD ? (io.specify?.[f] ?? "").trim() : "";
      const offered = withTypedOther(listed, (val) => wordOf(screen, tile, c, val) === OTHER_WORD, typed);
      const node = <PickerCell value={v} options={withHeld(offered, v)} placeholder={c.ph} ariaLabel={label} onCommit={(s) => saveInBackground(io.save({ [f]: s }))} testId={tid} />;
      // Rule 41: a typed "Other" opens its box under its own column.
      if (wordOf(screen, tile, c, v) === OTHER_WORD) {
        under.push(<OtherBox key={`${key}-other`} label={c.otherLabel} placeholder={c.otherPh} value={io.specify?.[f] ?? ""}
          onCommit={(t) => saveInBackground(io.saveSpecify(f, t))} testId={`${tid}-other`} />);
      }
      return { node, under };
    }
    if (c.kind === "seg") {
      return { node: <SegmentCell value={v} options={optionsOf(screen, tile, c)} ariaLabel={label} onCommit={(s) => saveInBackground(io.save({ [f]: s }))} testId={tid} />, under };
    }
    if (c.kind === "text" && c.long) {
      if (panels.isOpen(`${key}:big`)) under.push(<LocationBoxPanel key={`${key}-panel`} title={c.bigTitle ?? label} hint={c.bigHint} placeholder={c.bigPh} value={v}
        onCommit={(t) => io.save({ [f]: t })} onClose={() => panels.close(`${key}:big`)} testId={tid} />);
      return { node: <LocationBox value={v} placeholder={c.ph} open={panels.isOpen(`${key}:big`)} onOpen={() => panels.toggle(`${key}:big`)} onCommit={(t) => io.save({ [f]: t })} testId={tid} />, under };
    }
    if (c.kind === "text") {
      const money = isMoney(c);
      return { node: money
        ? <MoneyCell value={v} placeholder={c.ph} ariaLabel={label} onCommit={(t) => saveInBackground(io.save({ [f]: t }))} testId={tid} />
        : <IconTextCell value={v} placeholder={c.ph} ariaLabel={label} onCommit={(t) => saveInBackground(io.save({ [f]: t }))} testId={tid} />, under };
    }
    if (c.kind === "date") return { node: <DateCell value={v} ariaLabel={label} onCommit={(d) => saveInBackground(io.save({ [f]: d }))} testId={tid} />, under };
    if (c.kind === "address") {
      if (panels.isOpen(`${key}:addr`)) under.push(<AddressPanel key={`${key}-addr`} value={v} fieldId={io.fieldIdByKey.get(f) ?? f} onCommit={(a) => io.save({ [f]: a })}
        onClose={() => panels.close(`${key}:addr`)} testId={tid} />);
      const shown = formatAddressSummary(v);
      return { node: (
        <button type="button" onClick={() => panels.toggle(`${key}:addr`)} aria-expanded={panels.isOpen(`${key}:addr`)} aria-label={label}
          className={`${CELL_BOX} truncate px-3 text-left ${shown ? "" : "text-warm-muted"}`} data-testid={tid}>
          {shown || c.ph}
        </button>
      ), under };
    }
    if (c.kind === "person") {
      const fields = fieldsOf(screen, tile, c);
      const slots = slotsFor(fields, c.org);
      if (panels.isOpen(`${key}:card`)) under.push(<ContactCard interviewLook key={`${key}-card`} title={c.title ?? label} slots={slots} values={io.values} fieldIdByKey={io.fieldIdByKey}
        moreLabel={slots.length > 1 ? ASK_FOR_LABEL : undefined}
        clientId={io.clientId} onSave={io.save} onClose={() => panels.close(`${key}:card`)} testId={`${tid}-card`} />);
      return { node: <LinkedPersonCell value={io.values[fields[0]] ?? ""} word={c.word ?? ""} org={c.org} open={panels.isOpen(`${key}:card`)} onOpen={() => panels.toggle(`${key}:card`)} testId={tid} />, under };
    }
    return { node: <div />, under };
  };

  const tableRows: RowTableRow[] = data.map((io) => {
    const w = where(io);
    const built = columnCells.map((c) => (isDrawn(screen, tile, c) ? null : cellFor(c, io)));
    const shown = built.filter((b): b is { node: ReactNode; under: ReactNode[] } => b !== null);
    const below: ReactNode[] = [];
    // The row's own lines first (the owned-place block), then each answer's follow-ups, then the cards and panels.
    for (const c of lineCellsOf(rows, io)) {
      below.push(<Ch2Lines key={`${io.rowId}-lines`} where={{ ...w, testId: `${w.testId}-lines` }} lines={c.lines ?? []} io={io} noteFor={noteFor} onNote={onNote} packed />);
    }
    for (const c of columnCells) {
      if (isDrawn(screen, tile, c) || !(c.kind === "seg" || c.kind === "picker") || c.k === "will") continue;
      const branch = thenOf(w, c, io.values);
      if (branch.length) below.push(<Ch2Lines key={`${io.rowId}-${c.k}-then`} where={{ ...w, testId: `${w.testId}-${c.k}-then` }} lines={branch} io={io} noteFor={noteFor} onNote={onNote} />);
    }
    const under = shown.flatMap((b) => b.under);
    // s235 PR B (D-235.65): the old home's one line, kept on a folded row so it is always answerable.
    if (io.stillHere) {
      const sh = io.stillHere;
      under.unshift(<StillHereLine key={`${io.rowId}-still`} {...sh} testId={`${w.testId}-still`} />);
    }
    // s237 PR 5 (D-237.52, board v465 `:312-315`): the home row's one line, first under it.
    if (io.homeLine) {
      const hl = io.homeLine;
      under.unshift(
        <p key={`${io.rowId}-homeline`} className="text-[13px] text-warm-muted" data-testid={`${w.testId}-homeline`}>
          {hl.text}{" "}
          <button type="button" onClick={hl.onChange} className="text-sage-600 underline hover:text-sage-800" data-testid={`${w.testId}-homeline-change`}>{hl.change}</button>
        </p>,
      );
    }
    // s233 (board v417): the row's second line is its own lines and its answers' follow-ups; filled = any answer in the row.
    const filled = answerFields(screen, tile, rows.cells).some((k) => (io.values[k] ?? "").trim() !== "");
    return {
      rowId: io.rowId,
      lead: lead ?? "",
      leadCell: shown[0]?.node,
      cells: shown.slice(1).map((b) => b.node),
      canAddRow: true,
      note: {
        value: io.values[FIELD_KEYS.NOTES] ?? "",
        open: panels.isOpen(`${io.rowId}:note`),
        onToggle: () => panels.toggle(`${io.rowId}:note`),
        // s237 (D-237.30): the save is returned, so the Note's Done waits for it.
        onCommit: (t) => (t !== (io.values[FIELD_KEYS.NOTES] ?? "") ? io.save({ [FIELD_KEYS.NOTES]: t }) : undefined),
      },
      // s233 (D-232.84 item 4): what a first-line cell opens sits right under the first line, above the second.
      lineOnePanels: under.length ? <div className="mb-3 flex flex-col gap-4 pl-0.5" data-testid={`${w.testId}-under`}>{under}</div> : null,
      panels: below.length ? <div className="mb-3 flex flex-col gap-4 pl-0.5" data-testid={`${w.testId}-below`}>{below}</div> : null,
      fold: { hasSecond: below.length > 0, filled, ...secondLineCounts(screen, tile, [...columnCells, ...lineCellsOf(rows, io)], io) },
      noRemove: io.pinned,
      leadMark: io.mark ?? mark,
    };
  });

  // A table's yes/no carries its note too (the board's `noteFor` on a row cell — Firearms' "In a gun safe?"): the note
  // shows while any row holds an answer that has one.
  const tableNote = (() => {
    let note = "";
    for (const io of data) for (const c of columnCells.filter((x) => x.kind === "seg" && !isDrawn(screen, tile, x))) {
      const nf = noteFor?.[c.k ?? ""];
      const word = wordOf(screen, tile, c, io.values[fieldOf(screen, tile, c)] ?? "");
      if (!word || !nf) continue;
      if (typeof nf === "object") { if (nf[word]) note = nf[word]; } else if (word !== "No" && word !== "Not sure") note = nf;
    }
    return note;
  })();
  useEffect(() => { if (tableNote) onNote?.(tableNote); }, [tableNote, onNote]);

  return (
    <InterviewRowTable columns={headers.map((h, i) => ({ key: `c${i}`, label: h.label, sub: h.sub }))} rows={tableRows} gridTemplate={gridTemplate}
      rowsLead={lead} onAddRow={() => onAdd()} onRemove={onRemove} testIdPrefix={testId} foldRows rowMarks strongRowLine />
  );
}

/** s233 (D-233.81): a row's second-line blocks — a pinned row with `pinLines` asks those in place of the table's own. */
function lineCellsOf(rows: Ch2Rows, io: Ch2RowIO): readonly Ch2Cell[] {
  if (io.pinned && rows.pinLines?.length) return [{ kind: "lines", lines: rows.pinLines } as Ch2Cell];
  return rows.cells.filter((x) => x.kind === "lines");
}

/**
 * s233 (rule 147): how many questions a row's second line holds, and how many are still unanswered — its own lines and the
 * follow-ups its answers have opened (a person line's once someone is named), drawn cells and switched-off lines left out.
 * The fold's hover label reads it: "Show 4 more questions — 3 to answer".
 */
function secondLineCounts(screen: string, tile: string, cells: readonly Ch2Cell[], io: Ch2RowIO): { more: number; toAnswer: number } {
  let more = 0, toAnswer = 0;
  const walk = (ls: readonly Ch2Cell[], all: readonly Ch2Cell[]) => {
    for (const l of ls) {
      if (l.kind === "break" || isDrawn(screen, tile, l) || (l.k === "gift" && io.skipGift?.(io.values))) continue;
      if (isOff(screen, tile, l, all, io.values)) continue;
      const f = fieldOf(screen, tile, l);
      const v = io.values[f] ?? "";
      more++;
      if (!v.trim()) toAnswer++;
      if (Array.isArray(l.then)) { if (v.trim()) walk(l.then, all); continue; }
      const word = l.then ? branchWordOf(screen, tile, l, io.values) : "";
      if (word && l.then && !Array.isArray(l.then) && l.then[word]) walk(l.then[word], all);
    }
  };
  for (const c of cells) if (c.kind === "lines") walk(c.lines ?? [], c.lines ?? []);
  for (const c of cells) {
    if (c.kind === "lines" || isDrawn(screen, tile, c) || !(c.kind === "seg" || c.kind === "picker") || c.k === "will") continue;
    const word = branchWordOf(screen, tile, c, io.values);
    if (word && c.then && !Array.isArray(c.then) && c.then[word]) walk(c.then[word], c.then[word]);
  }
  return { more, toAnswer };
}

/** Every field a table row answers — its columns, its own lines and their follow-ups, drawn cells left out. */
function answerFields(screen: string, tile: string, cells: readonly Ch2Cell[]): string[] {
  const out: string[] = [];
  const walk = (cs: readonly Ch2Cell[]) => {
    for (const c of cs) {
      if (c.kind === "break" || isDrawn(screen, tile, c)) continue;
      if (c.kind === "lines") { walk(c.lines ?? []); continue; }
      out.push(...fieldsOf(screen, tile, c));
      if (Array.isArray(c.then)) walk(c.then);
      else if (c.then) for (const branch of Object.values(c.then)) walk(branch);
    }
  };
  walk(cells);
  return Array.from(new Set(out.filter(Boolean)));
}
